/**
 * WHICH ROUTE IS THE WORKSPACE, AND HOW A STATE TREE BECOMES A ROUTE
 * (launch-marketing-pages design M2).
 *
 * Route-structure helpers the boot path reads without the workspace mounted:
 * the sign-in entry, the help venue switch, and the sign-in landing. It imports
 * nothing from the app but the `?state=` codec leaf, so every one of those can
 * import it without a cycle and without reaching a workspace store (the
 * `services/sessionToken.js` rule). `stores/unifiedRouter.js` re-exports
 * `isWorkspaceRoute` and `workspaceRouteTarget`.
 */

import { encodeState } from '@/utils/workspaceStateCodec'

/**
 * THE ONE "ARE WE ON THE WORKSPACE" TEST (help-entry-and-article-links D5):
 * does any record in `matched` declare `meta.workspace`? Every record that
 * renders `MainView` does (`router/index.js`, `router/retiredAdminRoutes.js`),
 * and `matched` carries the parent records, so a child route inherits it.
 *
 * Its readers are the places that choose between the workspace and a page
 * outside it: `workspaceRouteTarget` below, the sign-in entry
 * (`useSignInEntry`), and the documentation store's help venue switch
 * (`viewArticle` / `viewCategory` / `viewHelpHome`). One predicate, so they can
 * never disagree about which page is which.
 *
 * @param {object} route - a route location.
 * @returns {boolean} whether that route renders the workspace.
 */
export function isWorkspaceRoute (route) {
  return Boolean(route.matched?.some((record) => record.meta?.workspace))
}

/**
 * WHERE A `?state=` WRITE LANDS (help-entry-and-article-links D5; spec
 * `help-destination-navigation` § "A panel navigation made outside the
 * workspace lands in the workspace"). Every URL write that carries the unified
 * router's state onto the current route resolves its target here.
 *
 * The param only means something on a route that renders the workspace, and
 * every record that renders `MainView` declares `meta.workspace`
 * (`router/index.js`, `router/retiredAdminRoutes.js`). On one, the write stays
 * where it is: the current name, params and every sibling query key, beside
 * the new `state`. Anywhere else — the public help pages, legal, marketing,
 * the guest-only forms — no panel renders the state, so the write lands on
 * `MainMap` carrying the state alone; the public page's own query means
 * nothing there. Written onto the public page instead, it would be a dead
 * click: "Ask Artemis" on `/help` would change the address and open nothing.
 *
 * Keyed on route STRUCTURE through `isWorkspaceRoute`, never on a list of
 * public route names, so a page added later is covered by construction.
 *
 * @param {object} route - the current route location.
 * @param {string} state - the compressed `?state=` param.
 * @returns {object} the route location to push.
 */
export function workspaceRouteTarget (route, state) {
  if (isWorkspaceRoute(route)) {
    return {
      name: route.name,
      query: {
        ...(route.query || {}),
        state
      },
      params: route.params || {}
    }
  }

  return { name: 'MainMap', query: { state } }
}

/**
 * A LANDING AS A ROUTE (launch-marketing-pages design M2, ruling L9): the
 * landing's location with the workspace state it opens encoded as the
 * `?state=` param a booting workspace reads, or the location unchanged when
 * the landing names no state.
 *
 * It is the one encoding of a landing on the address: a fresh page load
 * resolves it to an href (`stores/auth.js#landingHref`), and a landing with no
 * workspace mounted replaces to it, so the `WorkspaceRoot` that mounts reads
 * the state exactly as a page load does.
 *
 * @param {{ location: object, workspaceState: Object|null }} landing
 * @returns {object} the route location.
 */
export function landingRoute ({ location, workspaceState }) {
  if (!workspaceState) return location
  return { ...location, query: { ...(location.query ?? {}), state: encodeState(workspaceState) } }
}
